import {
  INVOICE_BUYER_IDENTITY_THRESHOLD_MINOR,
  TAX_INVOICE_THRESHOLD_MINOR,
} from "@stb/jurisdiction-au"
import { PRODUCT_CONFIG } from "@stb/product-config"
import {
  Banknote,
  Briefcase,
  Calculator,
  FileText,
  Mail,
  Printer,
  Users,
  Wrench,
  XCircle,
} from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { InvoiceIllustration } from "../../components/home/home-art"
import { InvoiceHeroMock, ListMock } from "../../components/landing/product-art"
import { type ProductPageContent, withIcons } from "../../components/landing/product-page"
import { aud, INVOICE_EXAMPLE as ex } from "./example-numbers"

/** Copy source: docs/design/landing/copy-products.md (/invoicing). Only what T412 ships today. */
const wholeAud = (cents: number) => aud(cents, true)
const buyerThreshold = wholeAud(INVOICE_BUYER_IDENTITY_THRESHOLD_MINOR)
const receiptThreshold = wholeAud(TAX_INVOICE_THRESHOLD_MINOR)

/** Last time a person opened the ATO page and compared it with this copy. */
export const ATO_TAX_INVOICES = {
  href: "https://www.ato.gov.au/businesses-and-organisations/gst-excise-and-indirect-taxes/gst/tax-invoices",
  checked: "28 October 2026",
  label: "ATO, tax invoices",
}

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const ato = (
  <a href={ATO_TAX_INVOICES.href} className={linkClass}>
    {ATO_TAX_INVOICES.label}
  </a>
)

export const INVOICING_SEO = {
  title: "Tax invoices for sole traders: create, send, track",
  description: `Make a tax invoice with the details the ATO lists, email the PDF and track who has paid. Free plan has ${PRODUCT_CONFIG.plans.free.invoicesMonthly} invoices a month.`,
}

const COPY: ProductPageContent = {
  path: "/invoicing",
  name: "Invoicing",
  tag: "Tax invoices",
  reviewed: "28 October 2026",
  h1: "Tax invoices for sole traders, sent in minutes",
  lead: "Pick a customer, add the items and send a PDF tax invoice with the details the ATO lists. See who has paid and who is overdue.",
  answer: {
    text: `A tax invoice for a sale of ${buyerThreshold} or more must show the buyer's identity or ABN, and it must say the total includes GST or show the GST amount. If you are not registered for GST you issue a plain invoice with no GST shown.`,
    sources: [{ label: ATO_TAX_INVOICES.label, href: ATO_TAX_INVOICES.href }],
    checked: ATO_TAX_INVOICES.checked,
  },
  cta: { label: "Start free trial", href: "/login" },
  secondaryCta: { label: "See pricing", href: "/pricing" },
  heroNote: `${PRODUCT_CONFIG.plans.free.invoicesMonthly} invoices a month on the Free plan.`,
  stats: [
    { value: "10%", label: "GST worked out for you" },
    {
      value: `${PRODUCT_CONFIG.plans.free.invoicesMonthly} a month`,
      label: "invoices on the Free plan",
    },
    { value: "PDF", label: "emailed and kept with the invoice" },
    { value: "Part pay", label: "payments and overdue status" },
  ],
  stepsTitle: "Create, send and get paid",
  steps: [
    {
      title: "Create",
      body: "Choose a customer and add items. GST is worked out for you.",
    },
    {
      title: "Send",
      body: "Email the PDF to your customer. The invoice number is set when you send, and you get a few seconds to undo.",
    },
    {
      title: "Track payments",
      body: "Record full or part payments. Invoices past their due date show as overdue.",
    },
  ],
  example: {
    title: "Example invoice",
    caption: "Example invoice with GST",
    columns: ["Item", "Amount"],
    rows: [
      ...ex.lines.map((l) => ({ header: l.description, cells: [l.amount] })),
      { header: "Subtotal", cells: [ex.subtotal] },
      { header: "GST (10%)", cells: [ex.gst] },
      { header: "Total", cells: [ex.total] },
    ],
    note: `Example only: invoice to ${ex.customer}. Amounts exclude GST before the 10% is added.`,
  },
  included: [
    {
      title: "Customers",
      body: "Names, emails and an optional ABN, in one list.",
    },
    {
      title: "Invoice or tax invoice",
      body: "Registered for GST: a Tax Invoice. Not registered: a plain invoice with no GST shown.",
    },
    {
      title: "GST worked out",
      body: "Ten per cent on each line, or on the whole invoice, whichever rounding you choose. Mark lines GST-free when they are.",
    },
    {
      title: "Numbering, PDF and email",
      body: "Automatic numbering, a PDF that matches the preview, and a retry if sending fails.",
    },
    {
      title: "Payments",
      body: "Record full or part payments, or mark an invoice paid. Overdue invoices are flagged.",
    },
    {
      title: "Print",
      body: "Once an invoice is sent, download its PDF and print it from your device.",
    },
  ],
  limits: {
    title: "No receipts, card payments or BAS",
    body: (
      <>
        We do not issue payment receipts, take card payments for you or lodge your BAS. Recording a
        payment marks the invoice; it does not move money. Kestly gives estimates and general
        information, not tax advice. Source: {ato}, checked {ATO_TAX_INVOICES.checked}.
      </>
    ),
  },
  who: [
    {
      title: "Tradies and mobile services",
      body: "Send the invoice from your desk the same day the job is done.",
    },
    {
      title: "Contractors and consultants",
      body: "Repeat customers, clear numbering, simple follow-up.",
    },
    {
      title: "Rideshare and delivery drivers with an ABN",
      body: "Invoice your own customers and keep the records in one place.",
    },
  ],
  plan: {
    rows: ["invoices"],
    note: "Invoicing is not part of the Logbook plan.",
  },
  faqTitle: "Invoicing questions",
  faq: [
    {
      question: "What must a tax invoice show?",
      answer: (
        <>
          The words Tax Invoice, your name and ABN, the date, a description of what you sold with
          the quantity and price, how much of each sale is taxable, and the GST amount or a
          statement that the total includes GST. From {buyerThreshold} it also needs the
          buyer&apos;s name or ABN. Source: {ato}, checked {ATO_TAX_INVOICES.checked}.
        </>
      ),
    },
    {
      question: "Do I need a tax invoice for small sales?",
      answer: (
        <>
          If a customer asks for one, you must provide a tax invoice within 28 days, unless the sale
          is {receiptThreshold} or less including GST. Source: {ato}, checked{" "}
          {ATO_TAX_INVOICES.checked}.
        </>
      ),
    },
    {
      question: "Can I print an invoice?",
      answer: "Yes, once it has been sent. Download its PDF and print it, or save it as a file.",
    },
    {
      question: "Do you issue payment receipts?",
      answer: "No. You can record payments against an invoice.",
    },
  ],
  related: [
    { label: "Tax invoice requirements", href: "/guides/tax-invoice-requirements" },
    { label: "Free invoice generator", href: "/invoice-generator" },
    { label: "Receipts and expenses", href: "/receipt-scanner" },
    { label: "How it works", href: "/how-it-works" },
    { label: "Pricing", href: "/pricing" },
    { label: "Mileage tracker", href: "/mileage-tracker" },
  ],
  band: {
    title: "Send your first tax invoice",
    description: `${TRIAL_DAYS}-day free trial. No card needed.`,
  },
}

const STEP_ART = [
  <InvoiceIllustration
    key="create"
    invoice={{
      no: "INV-0044",
      to: ex.customer,
      lines: ex.lines.map((l) => [l.description, l.amount]),
      subtotal: ex.subtotal,
      gst: ex.gst,
      total: ex.total,
    }}
  />,
  <ListMock
    key="send"
    rows={[
      {
        title: `Sent to ${ex.customer}`,
        sub: "PDF attached · Thu 8 Oct",
        badge: "Sent",
        tone: "success",
      },
    ]}
  />,
  <ListMock
    key="track"
    rows={[
      { title: `INV-0044 · ${ex.customer}`, sub: "Due Thu 22 Oct", badge: "Sent", tone: "primary" },
      {
        title: "INV-0038 · Westside Cafe",
        sub: "Due Tue 6 Oct",
        badge: "Overdue",
        tone: "destructive",
      },
    ]}
  />,
]

/** Copy above; icons and pictures (T854) are presentation only. */
export const INVOICING: ProductPageContent = {
  ...COPY,
  heroCard: <InvoiceHeroMock />,
  stats: withIcons(COPY.stats, [Calculator, FileText, Mail, Banknote]),
  steps: COPY.steps.map((step, i) => ({ ...step, art: STEP_ART[i], artOnMobile: i > 0 })),
  included: withIcons(COPY.included, [Users, FileText, Calculator, Mail, Banknote, Printer]),
  limits: { ...COPY.limits, icon: XCircle },
  who: withIcons(COPY.who, [Wrench, Briefcase, Users]),
}
